import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  tax: number;
}

export interface InvoiceReminder {
  _id?: string;
  recipient: "Client" | "Business Developer" | "Both";
  sentAt: string;
  sentBy?: { fullName?: string; email?: string } | string;
  note?: string;
}

export interface Invoice {
  _id: string;
  invoiceNumber: string;
  invoiceType: string;
  customTypeLabel?: string;
  acceptedMethods?: string[];
  paymentGateway?: string;
  recurring?: boolean;
  recurringInterval?: string;
  status: "Upcoming" | "Submitted" | "Escrowed" | "Released" | "Completed" | "Overdue";
  source: "auto" | "manual";
  projectSource: "Upwork" | "Direct" | "Invitation";
  project?: { _id: string; name: string; projectType?: string; source?: string } | null;
  client?: { _id: string; name: string; email?: string; phone?: string; company?: string } | null;
  upworkAccount?: { _id: string; name: string } | null;
  milestoneIndex?: number | null;
  milestoneTitle?: string;
  billingAddress?: string;
  contactEmail?: string;
  invoiceDate?: string;
  dueDate?: string | null;
  currency: string;
  description?: string;
  lineItems: InvoiceLineItem[];
  notes?: string;
  amount: number;
  paymentMethod?: string;
  paymentTerms?: string;
  paidAt?: string | null;
  attachments?: { _id: string; name: string; url: string; r2Key: string; mimeType: string; size: number; category: string }[];
  reminders?: InvoiceReminder[];
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceKpis {
  total: number;
  totalValue: number;
  upcoming: number;
  submitted: number;
  escrowed: number;
  released: number;
  completed: number;
  overdue: number;
}

export type InvoiceFilters = {
  from?: string;
  to?: string;
  status?: string;
  projectType?: string;
  client?: string;
  project?: string;
  paymentMethod?: string;
  search?: string;
};

interface State {
  invoices: Invoice[];
  kpis: InvoiceKpis | null;
  types: string[];
  statuses: string[];
  loading: boolean;
  fetchMeta: () => Promise<void>;
  fetchInvoices: (f?: InvoiceFilters) => Promise<void>;
  fetchKpis: (f?: InvoiceFilters) => Promise<void>;
  getInvoice: (id: string) => Promise<Invoice | null>;
  createInvoice: (body: any) => Promise<Invoice | null>;
  updateInvoice: (id: string, body: any) => Promise<boolean>;
  deleteInvoice: (id: string) => Promise<boolean>;
  markPaid: (id: string) => Promise<boolean>;
  sendReminder: (id: string, recipient: string, note?: string) => Promise<boolean>;
}

export const useInvoiceStore = create<State>((set, get) => ({
  invoices: [],
  kpis: null,
  types: [],
  statuses: [],
  loading: false,

  fetchMeta: async () => {
    try {
      const { data } = await axiosInstance.get("/invoices/meta");
      set({ types: data.types || [], statuses: data.statuses || [] });
    } catch { /* pickers fall back to empty */ }
  },

  fetchInvoices: async (f = {}) => {
    set({ loading: true });
    try {
      const { data } = await axiosInstance.get("/invoices", { params: f });
      set({ invoices: data.invoices || [] });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load invoices");
    } finally {
      set({ loading: false });
    }
  },

  fetchKpis: async (f = {}) => {
    try {
      const { data } = await axiosInstance.get("/invoices/kpis", { params: f });
      set({ kpis: data.kpis });
    } catch { /* silent */ }
  },

  getInvoice: async (id) => {
    try {
      const { data } = await axiosInstance.get(`/invoices/${id}`);
      return data.invoice as Invoice;
    } catch {
      toast.error("Failed to load invoice");
      return null;
    }
  },

  createInvoice: async (body) => {
    try {
      const { data } = await axiosInstance.post("/invoices", body);
      toast.success("Invoice created");
      await Promise.all([get().fetchInvoices(), get().fetchKpis()]);
      return data.invoice as Invoice;
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to create invoice");
      return null;
    }
  },

  updateInvoice: async (id, body) => {
    try {
      await axiosInstance.put(`/invoices/${id}`, body);
      toast.success("Invoice updated");
      await Promise.all([get().fetchInvoices(), get().fetchKpis()]);
      return true;
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to update invoice");
      return false;
    }
  },

  deleteInvoice: async (id) => {
    try {
      await axiosInstance.delete(`/invoices/${id}`);
      toast.success("Invoice deleted");
      await Promise.all([get().fetchInvoices(), get().fetchKpis()]);
      return true;
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to delete invoice");
      return false;
    }
  },

  markPaid: async (id) => {
    try {
      await axiosInstance.post(`/invoices/${id}/paid`);
      toast.success("Invoice marked as paid");
      await Promise.all([get().fetchInvoices(), get().fetchKpis()]);
      return true;
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to mark paid");
      return false;
    }
  },

  sendReminder: async (id, recipient, note) => {
    try {
      const { data } = await axiosInstance.post(`/invoices/${id}/reminder`, { recipient, note });
      toast.success(data.message || "Reminder sent");
      return true;
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to send reminder");
      return false;
    }
  },
}));
